import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ADPResourceService } from 'digital-core';
import { PreInitSettingsService } from './settings';
import { AppConstants } from '../constants';
import { ICanCancelResponse } from '../../model/cancellation/can-cancel-response.dto';
import { IPolicyCancelResponse } from '../../model/cancellation/policy-cancel-response.dto';
import { IConfirmContactInfoRequest, IConfirmContactInfoResponse } from '../../model/cancellation/confirm-contact-info-request.dto';
import { selectMockCanCancel, selectMockCancelResponse } from './cancellation.mock-data';

/**
 * Cancel-plan (LPP) eligibility check + submit.
 *
 * Backed by the delivered Elita-backed policy-cancellation middleware
 * endpoints (`policy-cancellation/can-cancel` / `policy-cancellation/cancel`),
 * keyed off `planNumber` + `dealerCode`. The identifier is sent on the wire
 * as `planNumber` (not `certificateNumber`) because the middleware's
 * `IdentityToken` authorization check branches on the param name — sending
 * `certificateNumber` routed it into the child/device-certificate validation
 * path (whose claims don't include the plan-level master certificate),
 * while `planNumber` routes it into the validation branch that matches the
 * token's `PlanNumbers` claim. Both calls stay gated behind the
 * `CancelPlan_UseMock` portal setting for local/dev fixture use; flipping
 * that flag to `false` (per client) is what calls the real middleware — no
 * code change needed. The middleware also has its own independent gate
 * (`CancellationPolicy_Allowed` / `CancellationPolicy_UsingElita`) that must
 * be enabled server-side for the same environment.
 */
@Injectable({
  providedIn: 'root',
})
export class CancellationService {
  private readonly middlewareUrl: string;
  private readonly accountPath: string;

  private readonly preInitSettingsService = inject(PreInitSettingsService);
  private readonly http = inject(HttpClient);
  private readonly resourceService = inject(ADPResourceService);

  constructor() {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare.accountPath;
  }

  private get useMock(): boolean {
    return this.resourceService.getPortalSetting('CancelPlan_UseMock')?.Value === 'true';
  }

  canCancel(planNumber: string, dealerCode: string): Observable<ICanCancelResponse> {
    if (this.useMock) {
      return of(selectMockCanCancel(planNumber));
    }
    const params = new HttpParams().set('planNumber', planNumber).set('dealerCode', dealerCode);
    return this.http
      .get<ICanCancelResponse>(`${this.middlewareUrl}${this.accountPath}${AppConstants.API_ENDPOINTS.CancelPlanEligibility}`, { params })
      .pipe(
        // A transport/server failure is NOT the same as a negative eligibility answer — propagate
        // it so the caller can surface an error and keep the customer out of the flow, rather than
        // masquerading as `cancellable: false` and sending them to a result screen that asserts a
        // specific block reason it never actually received.
        catchError((err) => throwError(() => err))
      );
  }

  cancelPlan(planNumber: string, dealerCode: string): Observable<IPolicyCancelResponse> {
    if (this.useMock) {
      return of(selectMockCancelResponse(planNumber));
    }
    return this.http
      .post<IPolicyCancelResponse>(`${this.middlewareUrl}${this.accountPath}${AppConstants.API_ENDPOINTS.CancelPlan}`, {
        planNumber,
        dealerCode,
      })
      .pipe(catchError((err) => throwError(() => err)));
  }

  /**
   * Confirm-info step (Step 1) — persists a name/email/phone/address correction on the plan's
   * primary customer before the customer proceeds to cancel. Replaces the old
   * `AddressValidationService.saveAddress()` call, which had no field for a name correction and
   * wrote phone to the wrong Elita contact-type bucket (see `.middleware-handoffs/confirm-contact-info-ui-handoff.md`).
   */
  confirmContactInfo(request: IConfirmContactInfoRequest): Observable<IConfirmContactInfoResponse> {
    if (this.useMock) {
      return of({ success: true });
    }
    return this.http
      .post<IConfirmContactInfoResponse>(`${this.middlewareUrl}${this.accountPath}${AppConstants.API_ENDPOINTS.ConfirmContactInfo}`, request)
      .pipe(catchError((err) => throwError(() => err)));
  }
}
